import { useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/site/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { MY_TZ, NIC_TZ, fmtIn, ymd, addDays, buildSessions } from "@/lib/schedule";

function SessionList({ sessions, testId }) {
  return (
    <ol className="flex flex-col gap-1.5 text-sm" data-testid={testId}>
      {sessions.map((s) => (
        <li key={s.day} className="flex flex-wrap justify-between gap-2 border-b border-dashed border-[#0B2A4A]/10 py-1.5">
          <span><span className="font-mono-ui font-bold text-[#F08A3C] mr-2">DAY {s.day}</span>{fmtIn(s.start_utc, MY_TZ)}</span>
          <span className="text-xs text-slate-500">Nicaragua: {fmtIn(s.start_utc, NIC_TZ, { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" })}</span>
        </li>
      ))}
    </ol>
  );
}

function TimeInputs({ perDay, times, setTimes, start }) {
  const set = (i, v) => setTimes((t) => (perDay ? t.map((x, j) => (j === i ? v : x)) : t.map(() => v)));
  const rows = perDay ? times.map((_, i) => i) : [0];
  return (
    <div className="flex flex-col gap-2">
      {rows.map((i) => (
        <label key={i} className="flex items-center justify-between gap-3 text-sm">
          <span className="text-slate-600">{perDay ? `Day ${i + 1} · ${addDays(start, i).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}` : "Daily lesson time"}</span>
          <input type="time" value={times[i]} onChange={(e) => set(i, e.target.value)} className="rounded-lg border border-[#0B2A4A]/20 px-3 py-1.5" data-testid={`booking-time-${perDay ? i + 1 : "all"}`} />
        </label>
      ))}
    </div>
  );
}

export function BookingCard() {
  const [booking, setBooking] = useState(undefined);
  const [busy, setBusy] = useState([]);
  const [start, setStart] = useState(null);
  const [perDay, setPerDay] = useState(false);
  const [times, setTimes] = useState(Array(7).fill("10:00"));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/me/booking").then((r) => setBooking(r.data)).catch(() => setBooking(null));
    api.get("/bookings/busy").then((r) => setBusy(r.data.dates)).catch(() => {});
  }, []);
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const blocked = (d) => d < today || [...Array(7)].some((_, i) => busy.includes(ymd(addDays(d, i))));
  const sessions = start ? buildSessions(start, times) : [];

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/me/booking", { start_date: ymd(start), tz: MY_TZ, sessions });
      setBooking(data);
      setEditing(false);
      toast.success("Schedule requested — Mr. Greg will confirm it");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (booking === undefined) return null;
  const showForm = !booking || editing || booking.status === "declined";
  return (
    <div className="bg-white rounded-3xl border border-[#0B2A4A]/10 p-6 sm:p-8" data-testid="booking-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xl font-bold text-[#0B2A4A]"><CalendarDays className="h-5 w-5 text-[#1A9AA8]" /> Schedule your 7 days with Mr. Greg</h2>
        {booking && <StatusBadge status={booking.status} testId="booking-status" />}
      </div>
      {!showForm ? (
        <div className="mt-5 flex flex-col gap-4">
          <p className="text-sm text-slate-600">{booking.status === "confirmed" ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="h-4 w-4" /> Confirmed — see you in class!</span> : "Requested — waiting for Mr. Greg to confirm."} Times shown in your time zone ({MY_TZ}).</p>
          <SessionList sessions={booking.sessions} testId="booking-sessions" />
          <button onClick={() => setEditing(true)} className="self-start rounded-xl border border-[#0B2A4A]/20 px-4 py-2 text-sm font-semibold" data-testid="booking-change-btn">Change dates</button>
        </div>
      ) : (
        <div className="mt-5 grid lg:grid-cols-[auto_1fr] gap-8">
          <div>
            <p className="text-sm text-slate-600 mb-2">Pick your Day 1 — the next 6 days follow automatically.</p>
            <Calendar mode="single" selected={start} onSelect={setStart} disabled={blocked}
              modifiers={{ inRange: start ? [...Array(7)].map((_, i) => addDays(start, i)) : [] }}
              modifiersClassNames={{ inRange: "!bg-[#1A9AA8]/20 rounded-md" }} className="rounded-2xl border border-[#0B2A4A]/10" data-testid="booking-calendar" />
          </div>
          <div className="flex flex-col gap-4">
            <label className="flex items-center gap-3 text-sm font-semibold text-[#0B2A4A]">
              <Switch checked={perDay} onCheckedChange={setPerDay} data-testid="booking-perday-switch" /> Different time each day
            </label>
            <TimeInputs perDay={perDay} times={times} setTimes={setTimes} start={start || today} />
            <p className="flex items-center gap-1.5 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> Your time zone: {MY_TZ}. Each lesson is 60 minutes.</p>
            {start ? <SessionList sessions={sessions} testId="booking-preview" /> : <p className="text-sm text-slate-500">Choose a start date on the calendar.</p>}
            <button onClick={submit} disabled={!start || saving} className="self-start rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold disabled:opacity-50" data-testid="booking-submit-btn">
              {saving ? "Sending..." : "Request these 7 days"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
